import React, { useState, useEffect, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Wand2, Loader2, AlertCircle, Check, Upload, Trash2, Minimize2, Maximize2, Plus, ChevronDown, User, Sparkles } from 'lucide-react'
import { useProjectStore, GenerationRequest } from '../../stores/projectStore'
import { useCharacterStore } from '../../stores/characterStore'
import { Character } from '../../types'
import CharactersList from './CharactersList'
import * as fal from '@fal-ai/serverless-client'

interface ImageGenerationDialogProps {
  open: boolean
  onClose: () => void
  sceneId: string | null
  projectId: string
}

type GenerationMode = 'seedream' | 'seedream-edit' | 'nano-banana' | 'nano-banana-edit' | 'nano-banana-pro' | 'nano-banana-pro-edit' | 'flux-pro' | 'character'

interface UploadedReferenceImage {
  file: File
  preview: string
  id: string
}

// Function to generate resolution presets based on aspect ratio
const getResolutionsForAspectRatio = (aspectRatio: string) => {
  switch (aspectRatio) {
    case '16:9':
      return [
        { label: '512×288', value: '512x288', width: 512, height: 288 },
        { label: '1024×576', value: '1024x576', width: 1024, height: 576 },
        { label: '1920×1080', value: '1920x1080', width: 1920, height: 1080 },
        { label: '2560×1440', value: '2560x1440', width: 2560, height: 1440 },
        { label: 'Custom', value: 'custom', width: 1920, height: 1080 },
      ]
    case '9:16':
      return [
        { label: '576×1024', value: '576x1024', width: 576, height: 1024 },
        { label: '1080×1920', value: '1080x1920', width: 1080, height: 1920 },
        { label: '1440×2560', value: '1440x2560', width: 1440, height: 2560 },
        { label: '2160×3840', value: '2160x3840', width: 2160, height: 3840 },
        { label: 'Custom', value: 'custom', width: 1080, height: 1920 },
      ]
    case '4:3':
      return [
        { label: '512×384', value: '512x384', width: 512, height: 384 },
        { label: '1024×768', value: '1024x768', width: 1024, height: 768 },
        { label: '1600×1200', value: '1600x1200', width: 1600, height: 1200 },
        { label: '2048×1536', value: '2048x1536', width: 2048, height: 1536 },
        { label: 'Custom', value: 'custom', width: 1024, height: 768 },
      ]
    case '4:5':
      return [
        { label: '512×640', value: '512x640', width: 512, height: 640 },
        { label: '1024×1280', value: '1024x1280', width: 1024, height: 1280 },
        { label: '1536×1920', value: '1536x1920', width: 1536, height: 1920 },
        { label: '2048×2560', value: '2048x2560', width: 2048, height: 2560 },
        { label: 'Custom', value: 'custom', width: 1024, height: 1280 },
      ]
    case '1:1':
      return [
        { label: '512×512', value: '512x512', width: 512, height: 512 },
        { label: '768×768', value: '768x768', width: 768, height: 768 },
        { label: '1024×1024', value: '1024x1024', width: 1024, height: 1024 },
        { label: '1536×1536', value: '1536x1536', width: 1536, height: 1536 },
        { label: 'Custom', value: 'custom', width: 1024, height: 1024 },
      ]
    default:
      return [
        { label: '512×512', value: '512x512', width: 512, height: 512 },
        { label: '768×768', value: '768x768', width: 768, height: 768 },
        { label: '1024×1024', value: '1024x1024', width: 1024, height: 1024 },
        { label: '512×768 (Portrait)', value: '512x768', width: 512, height: 768 },
        { label: '768×512 (Landscape)', value: '768x512', width: 768, height: 512 },
      ]
  }
}

const ASPECT_RATIOS = [
  { label: '16:9', value: '16:9' },
  { label: '9:16', value: '9:16' },
  { label: '1:1', value: '1:1' },
  { label: '4:3', value: '4:3' },
  { label: '3:4', value: '3:4' },
]

const FLUX_IMAGE_SIZES = [
  { label: 'Landscape 4:3', value: 'landscape_4_3' },
  { label: 'Landscape 16:9', value: 'landscape_16_9' },
  { label: 'Portrait 4:3', value: 'portrait_4_3' },
  { label: 'Portrait 16:9', value: 'portrait_16_9' },
  { label: 'Square', value: 'square' },
  { label: 'Square HD', value: 'square_hd' },
  { label: 'Custom', value: 'custom' }
]

// HD Resolution options for nano-banana-pro (1K/2K/4K)
const getHDResolutionsForAspectRatio = (aspectRatio: string) => {
  switch (aspectRatio) {
    case '16:9':
      return [
        { label: '1K (1024×576)', value: '1k', width: 1024, height: 576 },
        { label: '2K (2048×1152)', value: '2k', width: 2048, height: 1152 },
        { label: '4K (4096×2304)', value: '4k', width: 4096, height: 2304 },
      ]
    case '9:16':
      return [
        { label: '1K (576×1024)', value: '1k', width: 576, height: 1024 },
        { label: '2K (1152×2048)', value: '2k', width: 1152, height: 2048 },
        { label: '4K (2304×4096)', value: '4k', width: 2304, height: 4096 },
      ]
    case '4:3':
      return [
        { label: '1K (1024×768)', value: '1k', width: 1024, height: 768 },
        { label: '2K (2048×1536)', value: '2k', width: 2048, height: 1536 },
        { label: '4K (4096×3072)', value: '4k', width: 4096, height: 3072 },
      ]
    case '3:4':
      return [
        { label: '1K (768×1024)', value: '1k', width: 768, height: 1024 },
        { label: '2K (1536×2048)', value: '2k', width: 1536, height: 2048 },
        { label: '4K (3072×4096)', value: '4k', width: 3072, height: 4096 },
      ]
    case '1:1':
      return [
        { label: '1K (1024×1024)', value: '1k', width: 1024, height: 1024 },
        { label: '2K (2048×2048)', value: '2k', width: 2048, height: 2048 },
        { label: '4K (4096×4096)', value: '4k', width: 4096, height: 4096 },
      ]
    default:
      return [
        { label: '1K (1024×1024)', value: '1k', width: 1024, height: 1024 },
        { label: '2K (2048×2048)', value: '2k', width: 2048, height: 2048 },
        { label: '4K (4096×4096)', value: '4k', width: 4096, height: 4096 },
      ]
  }
}

const PROMPT_HELPERS = {
  camera: [
    'Cinematic Lighting', 'Wide Angle', 'Macro', 'Telephoto', 'Drone Shot', 'GoPro', 'Polaroid', 'Fish Eye', 'Bokeh', 'Depth of Field'
  ],
  style: [
    'Photorealistic', 'Anime', 'Oil Painting', 'Watercolor', 'Cyberpunk', 'Steampunk', 'Minimalist', 'Noir', 'Vintage', '3D Render', 'Concept Art'
  ],
  lighting: [
    'Golden Hour', 'Blue Hour', 'Studio Lighting', 'Natural Light', 'Neon Lights', 'Volumetric Lighting', 'God Rays', 'Dark and Moody', 'Bright and Airy'
  ],
  subject: [
    'Portrait', 'Landscape', 'Architecture', 'Interior', 'Product', 'Character', 'Vehicle', 'Animal', 'Abstract'
  ]
}

const ImageGenerationDialog: React.FC<ImageGenerationDialogProps> = ({
  open,
  onClose,
  sceneId,
  projectId
}) => {
  // Load saved state from localStorage
  const savedState = localStorage.getItem('imageGeneration_lastState')
  const initialState = savedState ? JSON.parse(savedState) : { prompt: '', resolution: '1024x1024', mode: 'seedream' }

  const [mode, setMode] = useState<GenerationMode>(initialState.mode || 'seedream')
  const [prompt, setPrompt] = useState(initialState.prompt)
  const [resolution, setResolution] = useState(initialState.resolution)
  const [aspectRatio, setAspectRatio] = useState('16:9') // Default for Nano Banana
  const [numImages, setNumImages] = useState(1)
  const [referenceImages, setReferenceImages] = useState<UploadedReferenceImage[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [isDraggingRef, setIsDraggingRef] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)

  // Flux Pro Settings
  const [fluxImageSize, setFluxImageSize] = useState('landscape_16_9')
  const [safetyTolerance, setSafetyTolerance] = useState('2')
  const [enableSafetyChecker, setEnableSafetyChecker] = useState(true)
  const [outputFormat, setOutputFormat] = useState('jpeg')
  const [activeHelperTab, setActiveHelperTab] = useState<keyof typeof PROMPT_HELPERS | null>(null)

  // Character LoRA Settings
  const [selectedCharacter, setSelectedCharacter] = useState<Character | null>(null)
  const [loraScale, setLoraScale] = useState(1.0)

  // HD Mode Settings (nano-banana-pro)
  const [hdResolution, setHdResolution] = useState('2k') // Default to 2K

  const { addToGenerationQueue, generationQueue, currentProject, clearQueue } = useProjectStore()
  const { characters, fetchCharacters } = useCharacterStore()

  // Fetch characters when dialog opens
  useEffect(() => {
    if (open && projectId) {
      fetchCharacters(projectId)
    }
  }, [open, projectId, fetchCharacters])
  const fileInputRefs = [useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null)]
  
  // Get resolutions based on project aspect ratio
  const RESOLUTIONS = currentProject ? getResolutionsForAspectRatio(currentProject.aspectRatio) : getResolutionsForAspectRatio('1:1')

  // Set default aspect ratio based on project
  useEffect(() => {
    if (currentProject?.aspectRatio) {
      setAspectRatio(currentProject.aspectRatio)
      
      // Also try to match flux image size
      if (currentProject.aspectRatio === '16:9') setFluxImageSize('landscape_16_9')
      else if (currentProject.aspectRatio === '9:16') setFluxImageSize('portrait_16_9')
      else if (currentProject.aspectRatio === '4:3') setFluxImageSize('landscape_4_3')
      else if (currentProject.aspectRatio === '1:1') setFluxImageSize('square')
    }
  }, [currentProject?.aspectRatio])

  // Load persisted reference images on mount
  useEffect(() => {
    if (open) {
      const savedImages = localStorage.getItem('imageGeneration_referenceImages')
      if (savedImages) {
        try {
          const parsed = JSON.parse(savedImages)
          // Restore images with preview data URIs
          const restored: UploadedReferenceImage[] = []
          for (const saved of parsed) {
            // Convert data URI back to File for uploading
            fetch(saved.preview)
              .then(res => res.blob())
              .then(blob => {
                const file = new File([blob], saved.file.name || 'image.png', { 
                  type: saved.file.type || 'image/png' 
                })
                restored.push({
                  file,
                  preview: saved.preview, // Use saved data URI as preview
                  id: saved.id
                })
                // Update state with restored images
                setReferenceImages([...restored])
              })
              .catch(err => console.error('Failed to restore image:', err))
          }
        } catch (error) {
          console.error('Failed to restore reference images:', error)
        }
      }
    }
  }, [open])

  // Configure fal.ai client
  useEffect(() => {
    const apiKey = import.meta.env.VITE_FAL_API_KEY
    if (apiKey) {
      fal.config({
        credentials: apiKey
      })
    }
  }, [])

  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (open && prompt) {
      localStorage.setItem('imageGeneration_lastState', JSON.stringify({
        prompt,
        resolution,
        mode
      }))
    }
  }, [prompt, resolution, mode, open])

  // Save reference images to localStorage whenever they change
  useEffect(() => {
    if (open && referenceImages.length > 0) {
      // Save the data URIs and metadata
      const toSave = referenceImages.map(img => ({
        preview: img.preview, // This is already a data URI
        id: img.id,
        file: {
          name: img.file.name,
          type: img.file.type,
          size: img.file.size
        }
      }))
      localStorage.setItem('imageGeneration_referenceImages', JSON.stringify(toSave))
    } else if (open && referenceImages.length === 0) {
      // Clear localStorage if no images
      localStorage.removeItem('imageGeneration_referenceImages')
    }
  }, [referenceImages, open])

  // Esc key handler
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open && !isUploading && !isMinimized) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [open, isUploading, onClose, isMinimized])

  const handleFileSelect = (index: number, files: FileList | null) => {
    if (!files || files.length === 0) return
    
    const file = files[0]
    if (!file.type.startsWith('image/')) return
    
    const newImage: UploadedReferenceImage = {
      file,
      preview: URL.createObjectURL(file),
      id: `${Date.now()}_${index}`
    }

    setReferenceImages(prev => {
      const updated = [...prev]
      // Remove old image at this index if exists
      const oldImage = updated.find(img => img.id.endsWith(`_${index}`))
      if (oldImage) {
        URL.revokeObjectURL(oldImage.preview)
        updated.splice(updated.indexOf(oldImage), 1)
      }
      updated.push(newImage)
      return updated
    })
  }

  const handleRemoveReference = (id: string) => {
    setReferenceImages(prev => {
      const image = prev.find(img => img.id === id)
      if (image) {
        URL.revokeObjectURL(image.preview)
      }
      return prev.filter(img => img.id !== id)
    })
  }

  const handleRefDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingRef(true)
  }

  const handleRefDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingRef(false)
  }

  const handleRefDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingRef(false)
    
    const files = e.dataTransfer.files
    if (!files || files.length === 0) return
    
    const file = files[0]
    if (!file.type.startsWith('image/')) return
    
    const newImage: UploadedReferenceImage = {
      file,
      preview: URL.createObjectURL(file),
      id: `${Date.now()}_${index}`
    }

    setReferenceImages(prev => {
      const updated = [...prev]
      // Remove old image at this index if exists
      const oldImage = updated.find(img => img.id.endsWith(`_${index}`))
      if (oldImage) {
        URL.revokeObjectURL(oldImage.preview)
        updated.splice(updated.indexOf(oldImage), 1)
      }
      updated.push(newImage)
      return updated
    })
  }

  const addToPrompt = (text: string) => {
    if (prompt) {
      // Check if it already ends with space or comma
      const separator = prompt.trim().endsWith(',') ? ' ' : prompt.trim().endsWith(' ') ? '' : ', '
      setPrompt((prev: string) => prev.trim() + separator + text)
    } else {
      setPrompt(text)
    }
  }

  const handleAddToQueue = async () => {
    if (!prompt.trim() || !sceneId) return
    
    // Validate required inputs for edit modes
    if ((mode === 'seedream-edit' || mode === 'nano-banana-edit' || mode === 'nano-banana-pro-edit') && referenceImages.length === 0) {
      alert('Please upload at least one reference image for Edit mode')
      return
    }

    // Validate character mode requires a selected character
    if (mode === 'character' && !selectedCharacter) {
      alert('Please select a character for Character mode')
      return
    }

    if (mode === 'character' && selectedCharacter?.status !== 'ready') {
      alert('Selected character is not ready for generation')
      return
    }

    setIsUploading(true)
    setUploadProgress(0)

    try {
      // Upload reference images to fal.storage if any
      const imageUrls: string[] = []
      if (referenceImages.length > 0) {
        console.log('Uploading reference images to fal.storage...')
        for (let i = 0; i < referenceImages.length; i++) {
          const refImg = referenceImages[i]
          try {
            const uploadedUrl = await fal.storage.upload(refImg.file)
            imageUrls.push(uploadedUrl)
            console.log('Uploaded reference image:', uploadedUrl)
            setUploadProgress(((i + 1) / referenceImages.length) * 100)
          } catch (uploadError) {
            console.error('Failed to upload reference image:', uploadError)
          }
        }
      }
      
      // Get HD resolution dimensions if in HD mode
      const hdResData = (mode === 'nano-banana-pro' || mode === 'nano-banana-pro-edit') 
        ? getHDResolutionsForAspectRatio(aspectRatio).find(r => r.value === hdResolution)
        : null

      // Add to Queue
      await addToGenerationQueue({
        prompt,
        mode,
        sceneId,
        projectId,
        settings: {
          resolution,
          aspectRatio,
          numImages: mode === 'flux-pro' || mode === 'character' || mode === 'nano-banana-pro-edit' ? 1 : numImages,
          imageUrls: imageUrls.length > 0 ? imageUrls : undefined,
          seed: Math.random() > 0.5 ? Math.floor(Math.random() * 1000000) : undefined,
          // Flux specific
          imageSize: fluxImageSize,
          safetyTolerance,
          enableSafetyChecker,
          outputFormat,
          // Character LoRA specific
          characterId: mode === 'character' ? selectedCharacter?.id : undefined,
          characterName: mode === 'character' ? selectedCharacter?.name : undefined,
          loraWeightsUrl: mode === 'character' ? selectedCharacter?.loraWeightsUrl : undefined,
          loraScale: mode === 'character' ? loraScale : undefined,
          // HD mode specific (nano-banana-pro)
          hdResolution: hdResData ? hdResolution : undefined,
          hdWidth: hdResData?.width,
          hdHeight: hdResData?.height
        }
      })

      // Reset prompt but keep settings
      setPrompt('')

    } catch (error) {
      console.error('Failed to add to queue:', error)
      alert('Failed to add request to queue')
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
    }
  }

  const handleClose = useCallback(() => {
    // Only cleanup locally, don't stop queue
    if (!isUploading) {
      referenceImages.forEach(img => URL.revokeObjectURL(img.preview))
      setReferenceImages([])
      onClose()
    }
  }, [isUploading, referenceImages, onClose])

  if (!open) return null

  const apiKey = import.meta.env.VITE_FAL_API_KEY
  const hasApiKey = !!apiKey && apiKey !== 'your_fal_api_key_here'

  // Minimized View
  if (isMinimized) {
    const pendingCount = generationQueue.filter(q => q.status === 'pending').length
    const processingItem = generationQueue.find(q => q.status === 'processing')

    return (
      <div className="fixed bottom-4 right-4 z-50">
        <motion.div
          className="glass-card p-4 w-72 shadow-2xl border border-white/20 bg-black/80 backdrop-blur-md rounded-xl"
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 20 }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center">
                <Wand2 className={`w-4 h-4 text-purple-300 ${processingItem ? 'animate-pulse' : ''}`} />
              </div>
              <div className="text-white text-sm font-medium">AI Generation</div>
            </div>
            <div className="flex items-center space-x-1">
              <button 
                onClick={() => setIsMinimized(false)}
                className="p-1.5 hover:bg-white/10 rounded-lg text-white/70 hover:text-white transition-colors"
                title="Maximize"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
              <button 
                onClick={handleClose}
                className="p-1.5 hover:bg-red-500/20 rounded-lg text-white/70 hover:text-red-300 transition-colors"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {processingItem ? (
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-white/70">
                  <span className="truncate pr-2">{processingItem.prompt}</span>
                  <span>{Math.round(processingItem.progress || 0)}%</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-1.5 overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300"
                    style={{ width: `${processingItem.progress || 0}%` }}
                  />
                </div>
                <div className="text-xs text-white/50">{processingItem.stage || 'Processing...'}</div>
              </div>
            ) : pendingCount > 0 ? (
              <div className="text-sm text-white/70 flex items-center space-x-2">
                <div className="w-2 h-2 bg-yellow-400 rounded-full" />
                <span>{pendingCount} items in queue</span>
              </div>
            ) : (
              <div className="text-sm text-white/50 text-center py-2">
                Queue empty
              </div>
            )}
            
            {pendingCount > 0 && processingItem && (
               <div className="text-xs text-white/50 border-t border-white/10 pt-2">
                 + {pendingCount} more pending
               </div>
            )}
          </div>
        </motion.div>
      </div>
    )
  }

  // Full Modal View
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleClose}
      >
        <motion.div
          className="glass-card w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden border border-white/20"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10 flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                <Wand2 className="w-5 h-5 text-purple-300" />
              </div>
              <div>
              <h2 className="text-xl font-headline text-white">Generate Image with AI</h2>
                <p className="text-xs text-white/50">Queue system active • Requests are processed sequentially</p>
            </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsMinimized(true)}
                className="glass-button p-2 hover:bg-white/20 text-white/70 hover:text-white"
                title="Minimize to background"
              >
                <Minimize2 className="w-5 h-5" />
              </button>
            <button
              onClick={handleClose}
                className="glass-button p-2 hover:bg-white/20 text-white/70 hover:text-white"
            >
                <X className="w-5 h-5" />
            </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {!hasApiKey ? (
              <div className="glass-card bg-yellow-500/20 border border-yellow-500/30 p-6">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-6 h-6 text-yellow-300 flex-shrink-0 mt-0.5" />
                  <div className="text-yellow-200">
                    <h3 className="font-semibold mb-2">API Key Required</h3>
                    <p className="text-sm mb-3">
                      To use AI image generation, you need to add your fal.ai API key.
                    </p>
                    <ol className="text-sm space-y-2 list-decimal list-inside">
                      <li>Get your API key from <a href="https://fal.ai/dashboard" target="_blank" rel="noopener noreferrer" className="underline hover:text-yellow-100">fal.ai/dashboard</a></li>
                      <li>Create a <code className="bg-black/30 px-1 py-0.5 rounded">.env.local</code> file in the project root</li>
                      <li>Add: <code className="bg-black/30 px-1 py-0.5 rounded">VITE_FAL_API_KEY=your_key_here</code></li>
                      <li>Restart the development server</li>
                    </ol>
                  </div>
                </div>
              </div>
            ) : !sceneId ? (
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                <h3 className="text-white font-medium mb-2">No Scene Selected</h3>
                <p className="text-white/70">Please select a scene first to generate images.</p>
              </div>
            ) : (
              <>
                {/* Model Mode Toggle - Full Width */}
                <div className="mb-6">
                  <div className="flex flex-wrap gap-2">
                    {[
                      { id: 'seedream', label: 'Seedream' },
                      { id: 'seedream-edit', label: 'Seedream Edit' },
                      { id: 'nano-banana', label: 'Nano Banana' },
                      { id: 'nano-banana-edit', label: 'Nano Banana Edit' },
                      { id: 'nano-banana-pro', label: 'HD', isHD: true },
                      { id: 'nano-banana-pro-edit', label: 'HD Edit', isHD: true },
                      { id: 'flux-pro', label: 'Flux 2 Pro' },
                      { id: 'character', label: 'Character', isCharacter: true }
                    ].map((m) => (
                      <button
                        key={m.id}
                        onClick={() => setMode(m.id as GenerationMode)}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center space-x-1.5 ${
                          mode === m.id
                            ? m.isCharacter 
                              ? 'bg-gray-600 text-white ring-2 ring-pink-500/50'
                              : m.isHD
                              ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white ring-2 ring-cyan-400/50'
                              : 'bg-gray-600 text-white ring-2 ring-white/30'
                            : m.isHD
                            ? 'bg-cyan-500/10 text-cyan-300 hover:text-cyan-200 hover:bg-cyan-500/20'
                            : 'bg-white/10 text-white/60 hover:text-white hover:bg-white/15'
                        }`}
                        disabled={isUploading}
                      >
                        {m.isCharacter && <User className="w-3.5 h-3.5" />}
                        {m.isHD && <Sparkles className="w-3.5 h-3.5" />}
                        <span>{m.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column: Form */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Character Selection (for character mode) */}
                {mode === 'character' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="text-white font-medium flex items-center space-x-2">
                        <Sparkles className="w-4 h-4 text-pink-400" />
                        <span>Select Character</span>
                      </label>
                      {selectedCharacter && (
                        <span className="text-xs text-pink-300 bg-pink-500/20 px-2 py-1 rounded">
                          Use trigger: <code className="font-mono">@{selectedCharacter.triggerWord}</code>
                        </span>
                      )}
                    </div>
                    
                    <CharactersList
                      projectId={projectId}
                      onSelectCharacter={setSelectedCharacter}
                      selectedCharacterId={selectedCharacter?.id}
                      compact={true}
                    />

                    {selectedCharacter && (
                      <div className="glass-card bg-white/5 p-4 rounded-lg space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-white/70 text-sm">LoRA Strength</span>
                          <span className="text-white text-sm font-mono">{loraScale.toFixed(1)}</span>
                        </div>
                        <input
                          type="range"
                          min="0.1"
                          max="1.5"
                          step="0.1"
                          value={loraScale}
                          onChange={(e) => setLoraScale(parseFloat(e.target.value))}
                          className="w-full accent-pink-500"
                        />
                        <div className="flex justify-between text-[10px] text-white/40">
                          <span>Subtle (0.1)</span>
                          <span>Balanced (1.0)</span>
                          <span>Strong (1.5)</span>
                        </div>
                        
                        <div className="mt-3 p-3 bg-pink-500/10 rounded-lg border border-pink-500/20">
                          <p className="text-xs text-pink-200">
                            <strong>Tip:</strong> Include <code className="bg-pink-500/30 px-1 rounded">@{selectedCharacter.triggerWord}</code> in your prompt to activate this character.
                          </p>
                          <p className="text-xs text-white/50 mt-1">
                            Example: "A photo of @{selectedCharacter.triggerWord} standing in a garden, golden hour lighting"
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                  {/* Reference Images Upload */}
                {(mode === 'seedream-edit' || mode === 'nano-banana-edit' || mode === 'nano-banana-pro-edit') && (
                  <div>
                    <label className="block text-white font-medium mb-3">
                      Reference Images <span className="text-white/60 text-sm font-normal">
                        (required for Edit modes{(mode === 'nano-banana-edit' || mode === 'nano-banana-pro-edit') ? ' - first image used' : ' - up to 4'})
                      </span>
                    </label>
                    <div className="grid grid-cols-4 gap-3">
                      {[0, 1, 2, 3].map((index) => {
                        const existingImage = referenceImages.find(img => img.id.endsWith(`_${index}`))
                        
                        return (
                          <div key={index} className="relative">
                            <input
                              ref={fileInputRefs[index]}
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileSelect(index, e.target.files)}
                              className="hidden"
                            />
                            
                            {existingImage ? (
                              <div className="relative aspect-square rounded-lg overflow-hidden glass-card border border-white/20 group">
                                <img
                                  src={existingImage.preview}
                                  alt={`Reference ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                                <button
                                  onClick={() => handleRemoveReference(existingImage.id)}
                                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500/90 hover:bg-red-600 flex items-center justify-center transition-colors shadow-lg"
                                  title="Remove image"
                                >
                                  <X className="w-3 h-3 text-white" />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => fileInputRefs[index].current?.click()}
                                onDragOver={(e) => handleRefDragOver(e, index)}
                                onDragLeave={handleRefDragLeave}
                                onDrop={(e) => handleRefDrop(e, index)}
                                className="w-full aspect-square rounded-lg border-2 border-dashed border-white/30 hover:border-purple-400/50 bg-white/5 hover:bg-purple-500/10 flex flex-col items-center justify-center transition-colors"
                                  disabled={isUploading}
                              >
                                <Upload className="w-6 h-6 text-white/50 mb-1" />
                                <span className="text-xs text-white/50">{index + 1}</span>
                              </button>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Prompt Input */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="text-white font-medium">
                      Prompt <span className="text-red-400">*</span>
                    </label>
                      
                      {/* Prompt Helpers Tabs */}
                      <div className="flex space-x-1">
                        {(Object.keys(PROMPT_HELPERS) as Array<keyof typeof PROMPT_HELPERS>).map((helper) => (
                          <button
                            key={helper}
                            onClick={() => setActiveHelperTab(activeHelperTab === helper ? null : helper)}
                            className={`text-xs px-2 py-1 rounded transition-colors ${
                              activeHelperTab === helper 
                                ? 'bg-purple-500 text-white' 
                                : 'text-white/50 hover:bg-white/10 hover:text-white/80'
                            }`}
                          >
                            + {helper.charAt(0).toUpperCase() + helper.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Helper Options Panel */}
                    <AnimatePresence>
                      {activeHelperTab && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="bg-white/5 rounded-lg p-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                            {PROMPT_HELPERS[activeHelperTab].map((item) => (
                          <button
                                key={item}
                                onClick={() => addToPrompt(item)}
                                className="text-left text-xs text-white/70 hover:text-white hover:bg-white/10 px-2 py-1.5 rounded transition-colors truncate"
                                title={item}
                              >
                                {item}
                          </button>
                        ))}
                      </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <textarea
                      value={prompt}
                      onChange={(e) => {
                        setPrompt(e.target.value)
                        // Auto-resize to fit content
                        e.target.style.height = 'auto'
                        e.target.style.height = Math.min(e.target.scrollHeight, 300) + 'px'
                      }}
                      onPaste={(e) => {
                        // Auto-resize after paste
                        setTimeout(() => {
                          const target = e.target as HTMLTextAreaElement
                          target.style.height = 'auto'
                          target.style.height = Math.min(target.scrollHeight, 300) + 'px'
                        }, 0)
                      }}
                      placeholder="Describe what you want to generate or edit..."
                      className="w-full glass-card bg-white/5 border border-white/20 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 placeholder:text-white/40 resize-y min-h-[80px] max-h-[300px]"
                      rows={3}
                      disabled={isUploading}
                    />
                    
                    {/* Settings Area */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {(mode === 'nano-banana-pro' || mode === 'nano-banana-pro-edit') ? (
                        <>
                          <div>
                            <label className="block text-white font-medium mb-2 text-sm">Aspect Ratio</label>
                            <select
                              value={aspectRatio}
                              onChange={(e) => setAspectRatio(e.target.value)}
                              disabled={isUploading}
                              className="w-full glass-card bg-white/5 border border-cyan-500/30 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 text-sm"
                            >
                              {ASPECT_RATIOS.map((ar) => (
                                <option key={ar.value} value={ar.value} className="bg-gray-800">{ar.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-white font-medium mb-2 text-sm">Resolution</label>
                            <div className="flex gap-2">
                              {getHDResolutionsForAspectRatio(aspectRatio).map((res) => (
                                <button
                                  key={res.value}
                                  onClick={() => setHdResolution(res.value)}
                                  disabled={isUploading}
                                  className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg transition-all ${
                                    hdResolution === res.value
                                      ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white ring-2 ring-cyan-400/50'
                                      : 'bg-white/10 text-white/60 hover:text-white hover:bg-white/15'
                                  }`}
                                >
                                  {res.value.toUpperCase()}
                                </button>
                              ))}
                            </div>
                            <p className="text-xs text-cyan-300/70 mt-1">
                              {getHDResolutionsForAspectRatio(aspectRatio).find(r => r.value === hdResolution)?.label}
                            </p>
                          </div>
                          {mode === 'nano-banana-pro' && (
                            <div>
                              <label className="block text-white font-medium mb-2 text-sm">Image Count</label>
                              <select
                                value={numImages}
                                onChange={(e) => setNumImages(Number(e.target.value))}
                                disabled={isUploading}
                                className="w-full glass-card bg-white/5 border border-cyan-500/30 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 text-sm"
                              >
                                {[1, 2, 3, 4].map((num) => (
                                  <option key={num} value={num} className="bg-gray-800">{num} Image{num > 1 ? 's' : ''}</option>
                                ))}
                              </select>
                            </div>
                          )}
                        </>
                      ) : mode === 'character' ? (
                        <>
                          <div className="col-span-1">
                            <label className="block text-white font-medium mb-2 text-sm">Image Size</label>
                            <select
                              value={fluxImageSize}
                              onChange={(e) => setFluxImageSize(e.target.value)}
                              className="w-full glass-card bg-white/5 border border-white/20 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-400 text-sm"
                            >
                              {FLUX_IMAGE_SIZES.map((size) => (
                                <option key={size.value} value={size.value} className="bg-gray-800">{size.label}</option>
                              ))}
                            </select>
                          </div>
                          
                          {fluxImageSize === 'custom' && (
                            <div className="col-span-1">
                              <label className="block text-white font-medium mb-2 text-sm">Custom Resolution</label>
                              <select
                                value={resolution}
                                onChange={(e) => setResolution(e.target.value)}
                                className="w-full glass-card bg-white/5 border border-white/20 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-400 text-sm"
                              >
                                {RESOLUTIONS.map((res) => (
                                  <option key={res.value} value={res.value} className="bg-gray-800">{res.label}</option>
                                ))}
                              </select>
                            </div>
                          )}

                          <div className="col-span-1 flex items-end space-x-4">
                            <label className="flex items-center space-x-2 cursor-pointer mb-2">
                              <input 
                                type="checkbox" 
                                checked={enableSafetyChecker}
                                onChange={(e) => setEnableSafetyChecker(e.target.checked)}
                                className="w-4 h-4 rounded border-gray-300 text-pink-600 focus:ring-pink-500 bg-white/10"
                              />
                              <span className="text-sm text-white/80">Safety Checker</span>
                            </label>
                            
                            <div className="flex-1">
                              <label className="block text-white font-medium mb-1 text-sm">Format</label>
                              <div className="flex bg-white/5 rounded p-0.5">
                                <button
                                  onClick={() => setOutputFormat('jpeg')}
                                  className={`flex-1 text-xs py-1 rounded transition-colors ${outputFormat === 'jpeg' ? 'bg-pink-600 text-white' : 'text-white/50'}`}
                                >
                                  JPEG
                                </button>
                                <button
                                  onClick={() => setOutputFormat('png')}
                                  className={`flex-1 text-xs py-1 rounded transition-colors ${outputFormat === 'png' ? 'bg-pink-600 text-white' : 'text-white/50'}`}
                                >
                                  PNG
                                </button>
                              </div>
                            </div>
                          </div>
                        </>
                      ) : mode === 'flux-pro' ? (
                        <>
                          <div className="col-span-1">
                             <label className="block text-white font-medium mb-2 text-sm">Image Size</label>
                             <select
                               value={fluxImageSize}
                               onChange={(e) => setFluxImageSize(e.target.value)}
                               className="w-full glass-card bg-white/5 border border-white/20 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 text-sm"
                             >
                               {FLUX_IMAGE_SIZES.map((size) => (
                                 <option key={size.value} value={size.value} className="bg-gray-800">{size.label}</option>
                               ))}
                             </select>
                          </div>
                          
                          {fluxImageSize === 'custom' && (
                             <div className="col-span-1">
                               <label className="block text-white font-medium mb-2 text-sm">Custom Resolution</label>
                               <select
                                  value={resolution}
                                  onChange={(e) => setResolution(e.target.value)}
                                  className="w-full glass-card bg-white/5 border border-white/20 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 text-sm"
                               >
                                  {RESOLUTIONS.map((res) => (
                                    <option key={res.value} value={res.value} className="bg-gray-800">{res.label}</option>
                                  ))}
                               </select>
                    </div>
                  )}

                          <div className="col-span-1">
                            <label className="block text-white font-medium mb-2 text-sm">Safety Tolerance (1-5)</label>
                             <div className="flex items-center space-x-2">
                               <input 
                                 type="range" 
                                 min="1" 
                                 max="5" 
                                 step="1" 
                                 value={safetyTolerance}
                                 onChange={(e) => setSafetyTolerance(e.target.value)}
                                 className="flex-1 accent-purple-500"
                               />
                               <span className="text-white text-sm w-4">{safetyTolerance}</span>
                             </div>
                             <div className="text-[10px] text-white/40 flex justify-between mt-1">
                               <span>Strict</span>
                               <span>Permissive</span>
                             </div>
                          </div>
                          
                          <div className="col-span-1 flex items-end space-x-4">
                            <label className="flex items-center space-x-2 cursor-pointer mb-2">
                              <input 
                                type="checkbox" 
                                checked={enableSafetyChecker}
                                onChange={(e) => setEnableSafetyChecker(e.target.checked)}
                                className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 bg-white/10"
                              />
                              <span className="text-sm text-white/80">Safety Checker</span>
                    </label>
                            
                            <div className="flex-1">
                              <label className="block text-white font-medium mb-1 text-sm">Format</label>
                              <div className="flex bg-white/5 rounded p-0.5">
                        <button
                                  onClick={() => setOutputFormat('jpeg')}
                                  className={`flex-1 text-xs py-1 rounded transition-colors ${outputFormat === 'jpeg' ? 'bg-purple-600 text-white' : 'text-white/50'}`}
                                >
                                  JPEG
                        </button>
                                <button
                                  onClick={() => setOutputFormat('png')}
                                  className={`flex-1 text-xs py-1 rounded transition-colors ${outputFormat === 'png' ? 'bg-purple-600 text-white' : 'text-white/50'}`}
                                >
                                  PNG
                                </button>
                    </div>
                  </div>
                </div>
                        </>
                      ) : mode.includes('seedream') ? (
                        <>
                          <div>
                            <label className="block text-white font-medium mb-2 text-sm">Resolution</label>
                            <select
                              value={resolution}
                              onChange={(e) => setResolution(e.target.value)}
                              disabled={isUploading}
                              className="w-full glass-card bg-white/5 border border-white/20 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 text-sm"
                            >
                              {RESOLUTIONS.map((res) => (
                                <option key={res.value} value={res.value} className="bg-gray-800">{res.label}</option>
                              ))}
                            </select>
                    </div>
                          <div>
                            <label className="block text-white font-medium mb-2 text-sm">Image Count</label>
                            <select
                              value={numImages}
                              onChange={(e) => setNumImages(Number(e.target.value))}
                              disabled={isUploading}
                              className="w-full glass-card bg-white/5 border border-white/20 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 text-sm"
                            >
                              {[1, 2, 3, 4].map((num) => (
                                <option key={num} value={num} className="bg-gray-800">{num} Image{num > 1 ? 's' : ''}</option>
                              ))}
                            </select>
                    </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <label className="block text-white font-medium mb-2 text-sm">Aspect Ratio</label>
                            <select
                              value={aspectRatio}
                              onChange={(e) => setAspectRatio(e.target.value)}
                              disabled={isUploading}
                              className="w-full glass-card bg-white/5 border border-white/20 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 text-sm"
                            >
                              {ASPECT_RATIOS.map((ar) => (
                                <option key={ar.value} value={ar.value} className="bg-gray-800">{ar.label}</option>
                              ))}
                            </select>
                  </div>
                          <div>
                            <label className="block text-white font-medium mb-2 text-sm">Image Count</label>
                            <select
                              value={numImages}
                              onChange={(e) => setNumImages(Number(e.target.value))}
                              disabled={isUploading}
                              className="w-full glass-card bg-white/5 border border-white/20 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 text-sm"
                            >
                              {[1, 2, 3, 4].map((num) => (
                                <option key={num} value={num} className="bg-gray-800">{num} Image{num > 1 ? 's' : ''}</option>
                              ))}
                            </select>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                <button
                    onClick={handleAddToQueue}
                    disabled={!prompt.trim() || isUploading || !sceneId || (mode === 'character' && (!selectedCharacter || selectedCharacter.status !== 'ready'))}
                  className={`w-full glass-button text-white font-semibold px-6 py-3 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    mode === 'character' 
                      ? 'bg-gradient-to-r from-pink-500/30 to-purple-500/30 hover:from-pink-500/40 hover:to-purple-500/40'
                      : (mode === 'nano-banana-pro' || mode === 'nano-banana-pro-edit')
                      ? 'bg-gradient-to-r from-cyan-500/30 to-blue-500/30 hover:from-cyan-500/40 hover:to-blue-500/40'
                      : 'bg-purple-500/30 hover:bg-purple-500/40'
                  }`}
                >
                    {isUploading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Uploading Resources... {Math.round(uploadProgress)}%</span>
                    </>
                  ) : mode === 'character' ? (
                    <>
                      <User className="w-5 h-5" />
                      <span>Generate with {selectedCharacter?.name || 'Character'}</span>
                    </>
                  ) : (mode === 'nano-banana-pro' || mode === 'nano-banana-pro-edit') ? (
                    <>
                      <Sparkles className="w-5 h-5" />
                      <span>Generate HD ({hdResolution.toUpperCase()})</span>
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-5 h-5" />
                        <span>Add to Queue</span>
                    </>
                  )}
                </button>
                </div>

                {/* Right Column: Queue Status */}
                <div className="lg:col-span-1 glass-card bg-black/20 rounded-lg p-4 flex flex-col h-full min-h-[300px]">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-white font-medium">Queue Status</h3>
                      <span className="text-white/50 text-sm">
                        {generationQueue.length} {generationQueue.length === 1 ? 'item' : 'items'}
                      </span>
                    </div>
                    {generationQueue.length > 0 && (
                      <button
                        onClick={() => clearQueue()}
                        className="glass-button text-xs px-3 py-1.5 text-red-300 hover:text-red-200 hover:bg-red-500/20 rounded-lg transition-colors border border-red-500/30"
                      >
                        Clear
                      </button>
                    )}
                  </div>

                  <div className="flex-1 overflow-y-auto space-y-3 custom-scrollbar">
                    {generationQueue.length === 0 ? (
                      <div className="text-white/30 text-center py-8 text-sm">
                        Queue is empty.<br/>Add a prompt to start generating.
                      </div>
                    ) : (
                      generationQueue.slice().reverse().map((req) => (
                        <div 
                          key={req.id} 
                          className={`p-3 rounded-lg border ${
                            req.status === 'processing' 
                              ? 'bg-purple-500/10 border-purple-500/30' 
                              : req.status === 'completed'
                              ? 'bg-green-500/5 border-green-500/20'
                              : req.status === 'failed'
                              ? 'bg-red-500/5 border-red-500/20'
                              : 'bg-white/5 border-white/10'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-1">
                            <span className="text-xs text-white/90 font-medium line-clamp-1" title={req.prompt}>
                              {req.prompt}
                            </span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                              req.status === 'processing' ? 'bg-purple-500/20 text-purple-300' :
                              req.status === 'completed' ? 'bg-green-500/20 text-green-300' :
                              req.status === 'failed' ? 'bg-red-500/20 text-red-300' :
                              'bg-white/10 text-white/50'
                            }`}>
                              {req.status}
                            </span>
                              </div>
                          
                          <div className="space-y-1">
                            {req.status === 'processing' && (
                              <div className="w-full bg-black/30 rounded-full h-1 overflow-hidden">
                                <div 
                                  className="h-full bg-purple-500 transition-all duration-300"
                                  style={{ width: `${req.progress || 0}%` }}
                                />
                              </div>
                            )}
                            <div className="flex justify-between text-[10px] text-white/50">
                              <span>
                                {req.mode === 'character' && req.settings.characterName 
                                  ? `Character: ${req.settings.characterName}` 
                                  : req.mode
                                } • {req.settings.numImages} img
                              </span>
                              <span>{req.stage}</span>
                              </div>
                            {req.error && (
                              <div className="text-[10px] text-red-400 leading-tight pt-1">
                                {req.error}
                            </div>
                          )}
                        </div>
                    </div>
                      ))
                    )}
                </div>
                </div>
              </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default ImageGenerationDialog
